/**
 * Composant d'affichage des reviews pour les propriétaires
 * Permet de visualiser les avis reçus et d'y répondre
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Rating,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  alpha,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Close as CloseIcon,
  Reply as ReplyIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  Visibility as VisibilityIcon,
  Build as BuildIcon,
  LocalShipping as ProcessIcon,
  EmojiEvents as ExperienceIcon,
  ExpandMore as ExpandMoreIcon,
  RateReview as RateReviewIcon,
  PhotoCamera as PhotoCameraIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ReportReviewButton from '../ReportReviewButton';

/**
 * Composant OwnerReviewDisplay
 * @param {Object} props
 * @param {Object} props.reservation - L'objet réservation avec reservationReview
 * @param {Function} props.onResponseAdded - Callback après ajout de réponse
 * @returns {React.ReactNode}
 */
const OwnerReviewDisplay = ({ reservation, onResponseAdded, userRole = 'owner' }) => {
  const theme = useTheme();
  const { t } = useTranslation('ownerReviews');
  const { t: tReviews } = useTranslation('reviews'); // Pour traduire les tags
  
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localReview, setLocalReview] = useState(reservation.reservationReview);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  const hasResponse = !!localReview.owner_response;
  const canRespond = userRole === 'owner';
  const canReportReview = userRole === 'owner';
  const canRenterReportResponse = userRole !== 'owner';  
  const charCount = response.length;
  const maxChars = 500;
  const isValid = response.trim().length > 0 && charCount <= maxChars;
  const moderationStatus = localReview?.moderation_status || reservation?.reservationReview?.moderation_status;
  // The admin can request changes either on the review itself or on the OWNER RESPONSE.
  // Backend stores response moderation status in `response_moderation_status`.
  const responseModerationStatus = localReview?.response_moderation_status || reservation?.reservationReview?.response_moderation_status;
  const changeRequested = (
    responseModerationStatus === 'changes_requested' ||
    moderationStatus === 'changes_requested'
  );

  // 🔍 DEBUG: Log pour diagnostiquer le problème d'édition
  console.log('🔍 [OwnerReviewDisplay] DEBUG INFO:', {
    hasResponse,
    canRespond,
    userRole,
    moderationStatus,
    responseModerationStatus,
    changeRequested,
    localReview_id: localReview?.id,
    localReview_owner_response: localReview?.owner_response,
    localReview_response_moderation_status: localReview?.response_moderation_status,
    reservation_id: reservation?.id,
    reservationReview_response_moderation_status: reservation?.reservationReview?.response_moderation_status
  });

  // Charger les photos à l'ouverture du modal de détails
  const handleOpenDetailsModal = async () => {
    setDetailsModalOpen(true);
    setActivePhotoIndex(0);
    
    // Charger les photos si elles ne sont pas déjà en cache
    if (!reviewPhotos.length && localReview.id) {
      setPhotosLoading(true);
      try {
        const response = await api.get(`/reviews/reservations/${localReview.id}/photos`);
        const photos = response.data?.data?.equipment_photos || [];
        setReviewPhotos(photos);
        if (photos.length > 0) {
          setActivePhotoIndex(0);
        }
        console.log(`✅ Photos chargées pour review ${localReview.id}:`, photos.length);
      } catch (err) {
        console.error('Erreur chargement photos review:', err);
        // Pas d'erreur bloquante, juste pas de photos
      } finally {
        setPhotosLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!detailsModalOpen) {
      setActivePhotoIndex(0);
    }
  }, [detailsModalOpen]);

  useEffect(() => {
    setActivePhotoIndex(0);
  }, [reviewPhotos]);

  // Keep localReview in sync with the reservation prop coming from the parent.
  // This ensures that moderation flags set by admins (possibly via another UI)
  // are reflected immediately in the owner's view.
  useEffect(() => {
    if (reservation && reservation.reservationReview) {
      setLocalReview(reservation.reservationReview);
    }
  }, [reservation?.reservationReview]);

  // Opens the response modal. If `initial` is provided, prefill the input (used for editing).
  const handleOpenModal = (initial = '') => {
    setResponse(initial || '');
    setError(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setResponse('');
    setError(null);
  };

  const handleSubmitResponse = async () => {
    if (!isValid) return;

    setLoading(true);
    setError(null);

    try {
      const result = await api.post(`/reviews/${localReview.id}/response`, {
        response: response.trim()
      });

      // ✅ Garder toutes les données existantes et mettre à jour uniquement les champs de réponse
      const updatedReview = {
        ...localReview, // Garder TOUTES les données existantes (ratings, comments, etc.)
        owner_response: response.trim(),
        owner_response_date: new Date().toISOString(),
        // Si c'était une modification suite à une demande de l'admin, le backend a réinitialisé le statut
        // On met à jour seulement si c'était 'changes_requested'
        ...(localReview.response_moderation_status === 'changes_requested' && {
          response_moderation_status: 'pending',
          response_moderation_notes: null
        })
      };
      
      setLocalReview(updatedReview);

      // Callback parent
      if (onResponseAdded) {
        onResponseAdded(updatedReview);
      }

      handleCloseModal();
    } catch (err) {
      console.error('Error adding response:', err);
      setError(err.response?.data?.message || t('modal.error'));
    } finally {
      setLoading(false);
    }
  };

  const getPhotoSource = (photo) => {
    if (!photo) {
      return '';
    }

    if (typeof photo === 'string') {
      return photo;
    }

    if (typeof photo === 'object') {
      return photo.url || photo.src || photo.path || '';
    }

    return '';
  };

  const handlePrevPhoto = () => {
    if (reviewPhotos.length <= 1) {
      return;
    }
    setActivePhotoIndex((prev) => (prev === 0 ? reviewPhotos.length - 1 : prev - 1));
  };

  const handleNextPhoto = () => {
    if (reviewPhotos.length <= 1) {
      return;
    }
    setActivePhotoIndex((prev) => (prev === reviewPhotos.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index) => {
    if (index === activePhotoIndex) {
      return;
    }
    setActivePhotoIndex(index);
  };

  const currentPhotoSrc = getPhotoSource(reviewPhotos[activePhotoIndex]);

  return (
    <>
      <Accordion
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          '&:before': {
            display: 'none'
          },
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: 1
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }
          }}
        >
          <RateReviewIcon sx={{ color: theme.palette.primary.main }} />
          <Stack spacing={0.5} flex={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              {canReportReview ? t('title') : t('myReviewTitle', 'Mon avis')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={
                  moderationStatus === 'approved' ? t('moderation.approvedLabel') :
                  moderationStatus === 'rejected' ? t('moderation.rejectedLabel') :
                  moderationStatus === 'changes_requested' ? t('moderation.changeRequestedLabel') :
                  t('moderation.pendingLabel')
                }
                size="small"
                color={
                  moderationStatus === 'approved' ? 'success' :
                  moderationStatus === 'rejected' ? 'error' :
                  moderationStatus === 'changes_requested' ? 'warning' :
                  'default'
                }
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 600
                }}
              />
            </Stack>
          </Stack>
          <Chip
            icon={<StarIcon sx={{ fontSize: 14 }} />}
            label={`${Number(localReview.overall_rating).toFixed(1)}/5`}
            size="small"
            color="warning"
            sx={{
              height: 22,
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          />
          {canReportReview && (
            <Box sx={{ ml: 'auto' }}>
              <ReportReviewButton
                reviewType="reservation_review"
                reviewId={localReview.id}
                size="small"
              />
            </Box>
          )}
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <Box>
        {/* Badge recommandation */}
        {localReview.would_recommend && (
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Chip
              size="small"
              icon={<ThumbUpIcon sx={{ fontSize: 14 }} />}
              label={t('recommends')}
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Box>
        )}

        {/* Rating */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Rating
            value={Number(localReview.overall_rating)}
            readOnly
            size="small"
            precision={0.5}
            sx={{
              '& .MuiRating-iconFilled': {
                color: theme.palette.warning.main
              }
            }}
          />
          <Typography variant="body2" fontWeight={600} color="warning.dark">
            {Number(localReview.overall_rating).toFixed(1)}/5
          </Typography>
        </Stack>

        {/* Comment */}
        {localReview.overall_comment && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mb: 1.5,
              fontStyle: 'italic',
              pl: 1,
              borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            "{localReview.overall_comment}"
          </Typography>
        )}

        {/* Tags */}
        {localReview.positive_tags && localReview.positive_tags.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={1.5}>
            {localReview.positive_tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tReviews(`tags.${tag}`)}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.dark
                }}
              />
            ))}
          </Stack>
        )}

        {/* Owner Response Section */}
          {hasResponse ? (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: changeRequested 
                ? alpha(theme.palette.warning.main, 0.08)
                : alpha(theme.palette.info.main, 0.08),
              border: changeRequested
                ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                : `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5} flexWrap="wrap">
              {changeRequested ? (
                <WarningIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
              ) : (
                <CheckIcon sx={{ fontSize: 14, color: theme.palette.info.main }} />
              )}
              <Typography 
                variant="caption" 
                fontWeight={600} 
                color={changeRequested ? 'warning.main' : 'info.main'}
              >
                {changeRequested 
                  ? t('moderation.changeRequestedLabel', 'Modifications requises')
                  : t('yourResponse')
                }
              </Typography>
              
              {/* Debug log */}
              {console.log('🔍 [OwnerReviewDisplay] responseModerationStatus:', responseModerationStatus, 'localReview:', localReview)}
              
              {/* Badge de statut de modération */}
              {(responseModerationStatus === 'pending' || responseModerationStatus === null) && (
                <Chip
                  label={t('moderation.pendingLabel', 'En attente de modération')}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    ml: 0.5,
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.dark,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                  }}
                />
              )}
              
              {responseModerationStatus === 'approved' && (
                <Chip
                  label={t('moderation.approvedLabel', 'Approuvé')}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    ml: 0.5,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.dark,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                  }}
                />
              )}
              
              {responseModerationStatus === 'rejected' && (
                <Chip
                  label={t('moderation.rejectedLabel', 'Rejeté')}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    ml: 0.5,
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.dark,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                  }}
                />
              )}
              
              {/* If admin requested changes and current user is owner, show edit button */}
              {changeRequested && canRespond && (
                <Box sx={{ ml: 'auto' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenModal(localReview.owner_response || '')}
                    aria-label={t('editResponse')}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Stack>
            
            {/* Message de modération de l'admin si demande de modification */}
            {changeRequested && localReview.response_moderation_notes && (
              <Alert 
                severity="warning" 
                icon={false}
                sx={{ 
                  mb: 1.5, 
                  py: 0.5,
                  fontSize: '0.75rem',
                  '& .MuiAlert-message': {
                    padding: 0
                  }
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  {t('moderation.moderationMessageLabel')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.3 }}>
                  {localReview.response_moderation_notes}
                </Typography>
              </Alert>
            )}
            
            {/* Message de modération de l'admin si reponse rejetée */}
            {responseModerationStatus === 'rejected' && localReview.response_moderation_notes && (
              <Alert 
                severity="error" 
                icon={false}
                sx={{ 
                  mb: 1.5, 
                  py: 0.5,
                  fontSize: '0.75rem',
                  '& .MuiAlert-message': {
                    padding: 0
                  }
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  {t('moderation.rejectionReasonLabel')}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.3 }}>
                  {localReview.response_moderation_notes}
                </Typography>
              </Alert>
            )}
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {localReview.owner_response}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {new Date(localReview.owner_response_date).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </Typography>
              {canRenterReportResponse && (
                <ReportReviewButton
                  reviewType="owner_response"
                  reviewId={localReview.id}
                  size="small"
                />
              )}
            </Stack>
          </Box>
        ) : null}

        {/* Boutons d'action */}
        <Stack direction="row" spacing={1} mt={1.5}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={handleOpenDetailsModal}
              fullWidth
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                fontWeight: 500
              }}
            >
              {t('details')}
            </Button>
            
            {canRespond && (!hasResponse || changeRequested) && moderationStatus !== 'rejected' && (
              <Button
                variant="contained"
                size="small"
                startIcon={<ReplyIcon />}
                onClick={() => handleOpenModal(localReview.owner_response || '')}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderRadius: 1,
                  fontWeight: 500
                }}
              >
                {hasResponse ? t('editResponse') || t('respond') : t('respond')}
              </Button>
            )}
          </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Modal de détails complets (lecture seule) */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              📋 {t('detailsModal.title')}
            </Typography>
            <IconButton size="small" onClick={() => setDetailsModalOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {/* Date d'évaluation */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            📅 {t('detailsModal.evaluatedOn')} {new Date(localReview.created_at).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
          </Typography>

          {/* Note globale */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.warning.main, 0.05) }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <StarIcon sx={{ color: theme.palette.warning.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('detailsModal.overallRating')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Rating value={Number(localReview.overall_rating)} readOnly precision={0.5} />
              <Typography variant="h6" fontWeight={600} color="warning.dark">
                {Number(localReview.overall_rating).toFixed(1)}/5
              </Typography>
            </Stack>
            {localReview.overall_comment && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                💬 "{localReview.overall_comment}"
              </Typography>
            )}
          </Paper>

          {/* Notes détaillées */}
          <Stack spacing={2}>
            {/* Équipement */}
            {localReview.equipment_rating && (
              <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <BuildIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('detailsModal.equipment')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <Rating value={Number(localReview.equipment_rating)} readOnly size="small" precision={0.5} />
                  <Typography variant="body2" fontWeight={600}>
                    {Number(localReview.equipment_rating).toFixed(1)}/5
                  </Typography>
                </Stack>
                
                
                
                {/* Sous-critères équipement */}
                <Stack spacing={1.5}>
                  {localReview.equipment_quality_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.equipmentQuality')}
                        </Typography>
                        <Rating value={Number(localReview.equipment_quality_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.equipment_quality_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.equipment_quality_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                          {localReview.equipment_quality_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {localReview.equipment_cleanliness_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.equipmentCleanliness')}
                        </Typography>
                        <Rating value={Number(localReview.equipment_cleanliness_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.equipment_cleanliness_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.equipment_cleanliness_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                          {localReview.equipment_cleanliness_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {localReview.equipment_state_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.equipmentState')}
                        </Typography>
                        <Rating value={Number(localReview.equipment_state_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.equipment_state_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.equipment_state_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}` }}>
                          {localReview.equipment_state_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Processus */}
            {localReview.process_rating && (
              <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.03) }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ProcessIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('detailsModal.process')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <Rating value={Number(localReview.process_rating)} readOnly size="small" precision={0.5} />
                  <Typography variant="body2" fontWeight={600}>
                    {Number(localReview.process_rating).toFixed(1)}/5
                  </Typography>
                </Stack>
                
               
                
                {/* Sous-critères processus */}
                <Stack spacing={1.5}>
                  {localReview.process_pickup_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.processPickup')}
                        </Typography>
                        <Rating value={Number(localReview.process_pickup_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.process_pickup_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.process_pickup_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
                          {localReview.process_pickup_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {localReview.process_return_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.processReturn')}
                        </Typography>
                        <Rating value={Number(localReview.process_return_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.process_return_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.process_return_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
                          {localReview.process_return_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {localReview.process_communication_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.processCommunication')}
                        </Typography>
                        <Rating value={Number(localReview.process_communication_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.process_communication_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.process_communication_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
                          {localReview.process_communication_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Expérience */}
            {localReview.experience_rating && (
              <Paper elevation={0} sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.03) }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ExperienceIcon sx={{ fontSize: 20, color: theme.palette.info.main }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('detailsModal.experience')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <Rating value={Number(localReview.experience_rating)} readOnly size="small" precision={0.5} />
                  <Typography variant="body2" fontWeight={600}>
                    {Number(localReview.experience_rating).toFixed(1)}/5
                  </Typography>
                </Stack>
                
                
                
                {/* Sous-critères expérience */}
                <Stack spacing={1.5}>
                  {localReview.experience_value_rating && (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          {t('detailsModal.experienceValue')}
                        </Typography>
                        <Rating value={Number(localReview.experience_value_rating)} readOnly size="small" precision={0.5} sx={{ fontSize: '0.9rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {Number(localReview.experience_value_rating).toFixed(1)}/5
                        </Typography>
                      </Stack>
                      {localReview.experience_value_comment && (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: `2px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                          {localReview.experience_value_comment}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>

          {/* Tags */}
          {(localReview.positive_tags?.length > 0 || localReview.negative_tags?.length > 0) && (
            <Box sx={{ mt: 2 }}>
              {localReview.positive_tags?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="success.main" sx={{ display: 'block', mb: 0.5 }}>
                    ✅ {t('detailsModal.positivePoints')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {localReview.positive_tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tReviews(`tags.${tag}`)}
                        size="small"
                        sx={{
                          mb: 0.5,
                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.dark
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {localReview.negative_tags?.length > 0 && (
                <Box>
                  <Typography variant="caption" fontWeight={600} color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
                    ⚠️ {t('detailsModal.pointsToImprove')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {localReview.negative_tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tReviews(`tags.${tag}`)}
                        size="small"
                        sx={{
                          mb: 0.5,
                          backgroundColor: alpha(theme.palette.warning.main, 0.1),
                          color: theme.palette.warning.dark
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}

          {/* Photos de l'évaluation */}
          {photosLoading ? (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('detailsModal.loadingPhotos', 'Chargement des photos...')}
              </Typography>
            </Box>
          ) : reviewPhotos.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PhotoCameraIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  📸 {t('detailsModal.photos')} ({reviewPhotos.length})
                </Typography>
              </Stack>

              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <Box
                  component="img"
                  src={currentPhotoSrc}
                  alt={t('detailsModal.photoAlt', 'Photo de l\'évaluation')}
                  onClick={() => currentPhotoSrc && setSelectedPhoto(currentPhotoSrc)}
                  sx={{
                    width: '100%',
                    maxHeight: 360,
                    objectFit: 'cover',
                    display: 'block',
                    cursor: currentPhotoSrc ? 'zoom-in' : 'default',
                    backgroundColor: alpha(theme.palette.common.black, 0.04)
                  }}
                />

                {reviewPhotos.length > 1 && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePrevPhoto();
                      }}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 12,
                        transform: 'translateY(-50%)',
                        color: 'primary.contrastText',
                        backgroundColor: alpha(theme.palette.common.black, 0.45),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.common.black, 0.6)
                        }
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleNextPhoto();
                      }}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 12,
                        transform: 'translateY(-50%)',
                        color: 'primary.contrastText',
                        backgroundColor: alpha(theme.palette.common.black, 0.45),
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.common.black, 0.6)
                        }
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 16,
                        bgcolor: alpha(theme.palette.common.black, 0.55),
                        color: '#fff',
                        px: 1.2,
                        py: 0.25,
                        borderRadius: 999,
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}
                    >
                      {activePhotoIndex + 1} / {reviewPhotos.length}
                    </Box>
                  </>
                )}
              </Box>

              {reviewPhotos.length > 1 && (
                <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap">
                  {reviewPhotos.map((photo, index) => {
                    const photoSrc = getPhotoSource(photo);
                    return (
                      <Box
                        key={`owner-review-photo-${index}`}
                        onClick={() => handleThumbnailClick(index)}
                        sx={{
                          position: 'relative',
                          width: 80,
                          height: 60,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          outline: index === activePhotoIndex
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${alpha(theme.palette.common.black, 0.15)}`,
                          boxShadow: index === activePhotoIndex ? 4 : 0,
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        <Box
                          component="img"
                          src={photoSrc}
                          alt={t('detailsModal.photoThumbnailAlt', 'Miniature photo')}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          ) : null}

          {/* Recommandation */}
          {localReview.would_recommend !== null && (
            <Alert 
              severity={localReview.would_recommend ? "success" : "warning"} 
              sx={{ mt: 2 }}
              icon={localReview.would_recommend ? <ThumbUpIcon /> : null}
            >
              <Typography variant="body2" fontWeight={600}>
                {localReview.would_recommend 
                  ? `👍 ${t('detailsModal.recommends')}`
                  : `👎 ${t('detailsModal.notRecommend')}`
                }
              </Typography>
            </Alert>
          )}

          {/* Réponse du propriétaire si elle existe */}
          {hasResponse && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.08),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                <CheckIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" fontWeight={600} color="info.main">
                  {t('yourResponse')}
                </Typography>
                {/* Allow editing from the details modal as well when admin requested changes */}
                {changeRequested && canRespond && (
                  <Box sx={{ ml: 'auto' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(localReview.owner_response || '')}
                      aria-label={t('editResponse')}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Stack>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {localReview.owner_response}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {new Date(localReview.owner_response_date).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailsModalOpen(false)} variant="contained">
            {t('detailsModal.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de réponse */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {t('modal.title')}
            </Typography>
            <IconButton size="small" onClick={handleCloseModal}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {/* Affichage de la review */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: alpha(theme.palette.warning.main, 0.05),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Rating value={Number(localReview.overall_rating)} readOnly size="small" />
              <Typography variant="body2" fontWeight={600}>
                {Number(localReview.overall_rating).toFixed(1)}/5
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {localReview.overall_comment}
            </Typography>
          </Paper>

          {/* Champ de saisie */}
          <TextField
            fullWidth
            multiline
            rows={4}
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            placeholder={t('modal.placeholder')}
            variant="outlined"
            error={charCount > maxChars}
            helperText={
              <Stack direction="row" justifyContent="space-between">
                <span>{charCount > maxChars ? t('modal.tooLong') : ''}</span>
                <span style={{ color: charCount > maxChars ? theme.palette.error.main : theme.palette.text.secondary }}>
                  {charCount}/{maxChars}
                </span>
              </Stack>
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1
              }
            }}
          />

          {/* Conseils */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              💡 {t('modal.tips')}
            </Typography>
          </Alert>

          {/* Erreur */}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} disabled={loading}>
            {t('modal.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitResponse}
            disabled={!isValid || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <ReplyIcon />}
          >
            {loading ? t('modal.sending') : t('modal.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal d'affichage photo en plein écran */}
      <Dialog
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }
        }}
      >
        <IconButton
          onClick={() => setSelectedPhoto(null)}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'white',
            backgroundColor: alpha(theme.palette.common.black, 0.5),
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.black, 0.7)
            },
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            p: 2
          }}
        >
          {selectedPhoto && (
            <img
              src={selectedPhoto}
              alt={t('detailsModal.fullscreenPhotoAlt', 'Photo en plein écran')}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default OwnerReviewDisplay;
